import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useParams } from "react-router-dom";
import TableLabels from "./TableLabels";
import {
  LuSearch,
  LuX,
  LuPackage,
  LuPackageOpen,
  LuUser,
} from "react-icons/lu";

const FALLBACK_IMAGE =
  "https://ui-avatars.com/api/?name=P&background=ffe2de&color=fff&size=200";

const PaymentRequestsTableRow = ({
  payments_list,
  alt_name,
  handleLoader,
  counter,
}) => {
  const { slug, lang } = useParams();
  const [_] = useTranslation("global");
  const role = localStorage.getItem("role");
  const [search, setSearch] = useState("");

  const rawOrders = payments_list?.data?.[0] || [];
  const rawMarketers = payments_list?.data?.[1] || [];
  const showMarketers = role !== "seller" && Array.isArray(rawMarketers);

  // Group marketers by product_id
  const marketersByProduct = useMemo(() => {
    if (!showMarketers) return {};
    const map = {};
    rawMarketers.forEach((marketer) => {
      const pid = marketer.product_id;
      if (!map[pid]) {
        map[pid] = [];
      }
      if (!map[pid].some((m) => m.marketer_id === marketer.marketer_id)) {
        map[pid].push(marketer);
      }
    });
    return map;
  }, [rawMarketers, showMarketers]);

  // Filter orders by search keyword
  const filteredOrders = useMemo(() => {
    if (!search.trim()) return rawOrders;
    const q = search.toLowerCase().trim();
    return rawOrders.filter((order) => {
      const title = (order.title || "").toLowerCase();
      const sku = (order.sku || "").toLowerCase();
      const pid = String(order.product_id || "");
      const color = (order.color || "").toLowerCase();
      const size = (order.size || order.Size || "").toLowerCase();
      const sellerName = `${order.first_name || ""} ${
        order.last_name || ""
      }`.toLowerCase();
      const sellerId = String(order.seller_id || "");

      // Check marketers for this product
      const productMarketers = marketersByProduct[order.product_id] || [];
      const matchMarketer = productMarketers.some((m) => {
        const mName = `${m.first_name || ""} ${m.last_name || ""}`.toLowerCase();
        const mId = String(m.marketer_id || "");
        return mName.includes(q) || mId.includes(q);
      });

      return (
        title.includes(q) ||
        sku.includes(q) ||
        pid.includes(q) ||
        color.includes(q) ||
        size.includes(q) ||
        sellerName.includes(q) ||
        sellerId.includes(q) ||
        matchMarketer
      );
    });
  }, [rawOrders, search, marketersByProduct]);

  return (
    <div className="pickup-card">
      <div className="pickup-card-header">
        <div className="pickup-card-title">
          <LuPackage size={20} />
          <span>{_("panel.pickupReport")}</span>
          <span className="badge bg-light text-secondary border ms-2">
            {filteredOrders.length}
          </span>
        </div>

        <div className="pickup-search-box">
          <LuSearch className={`pickup-search-icon ${lang === "en" ? "ltr" : ""}`} size={16} />
          <input
            type="text"
            className={`pickup-search-input ${lang === "en" ? "ltr" : ""}`}
            placeholder={
              lang === "ar"
                ? "بحث بالمنتج، SKU، المسوق، التاجر..."
                : "Search by product, SKU, marketer..."
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className={`pickup-search-clear ${lang === "en" ? "ltr" : ""}`}
              onClick={() => setSearch("")}
              title={lang === "ar" ? "مسح البحث" : "Clear search"}
            >
              <LuX size={15} />
            </button>
          )}
        </div>
      </div>

      <div className="pickup-table-wrapper">
        <table className="pickup-table">
          <thead>
            <TableLabels showMarketers={showMarketers} />
          </thead>
          <tbody>
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order, index) => {
                const remaining = (order.left || 0) - order.total_quantity;
                const productMarketers =
                  marketersByProduct[order.product_id] || [];

                return (
                  <tr key={index}>
                    {/* Product Cell */}
                    <td>
                      <div className="pickup-product-item">
                        <img
                          className="pickup-product-thumb"
                          src={order.thumbnail || FALLBACK_IMAGE}
                          alt={order.title}
                          onError={(e) => {
                            e.target.src = FALLBACK_IMAGE;
                          }}
                        />
                        <div className="pickup-product-title">
                          {order.title}
                        </div>
                        <span className="pickup-sku-tag">
                          SKU: {order.sku || "-"}
                        </span>
                        {(order.color || order.size || order.Size) && (
                          <div className="pickup-variants-row">
                            {order.color && (
                              <span className="pickup-variant-pill">
                                {order.color}
                              </span>
                            )}
                            {(order.size || order.Size) && (
                              <span className="pickup-variant-pill">
                                {order.size || order.Size}
                              </span>
                            )}
                          </div>
                        )}
                        <NavLink
                          to={`/${slug}/${lang}/${
                            role === "admin"
                              ? `panel/products/?code=${order.product_id}`
                              : ""
                          }`}
                          className="pickup-product-id-link"
                        >
                          #{order.product_id}
                        </NavLink>
                      </div>
                    </td>

                    {/* Orders count */}
                    <td>
                      <span className="fw-semibold text-dark">
                        {order.order_number}
                      </span>
                    </td>

                    {/* Total quantity ordered */}
                    <td>
                      <span className="fw-bold fs-6 text-dark">
                        {order.total_quantity}
                      </span>
                    </td>

                    {/* Quantity in warehouse */}
                    <td>
                      <span className="fw-semibold text-secondary">
                        {order.left || 0}
                      </span>
                    </td>

                    {/* Remaining after pickup */}
                    <td>
                      <span
                        className={`pickup-remaining-badge ${
                          remaining < 0
                            ? "negative"
                            : remaining > 0
                            ? "positive"
                            : "zero"
                        }`}
                      >
                        {remaining}
                      </span>
                    </td>

                    {/* Marketers */}
                    {showMarketers && (
                      <td>
                        {productMarketers.length > 0 ? (
                          <div className="pickup-pill-group">
                            {productMarketers.map((marketer) => (
                              <span
                                key={marketer.marketer_id}
                                className="pickup-pill"
                              >
                                <LuUser size={12} />
                                <span>
                                  {marketer.first_name} {marketer.last_name}
                                </span>
                                <span className="pickup-pill-id">
                                  #{marketer.marketer_id}
                                </span>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    )}

                    {/* Seller */}
                    <td>
                      <div className="pickup-seller-info">
                        <span className="pickup-seller-name">
                          {order.first_name} {order.last_name}
                        </span>
                        <span className="pickup-seller-id">
                          #{order.seller_id}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={showMarketers ? 7 : 6}
                  className="pickup-empty-state"
                >
                  <LuPackageOpen className="pickup-empty-icon" />
                  <p className="pickup-empty-text">
                    {search
                      ? lang === "ar"
                        ? "لا توجد نتائج مطابقة لبحثك"
                        : "No matching results found"
                      : lang === "ar"
                      ? "لا توجد طلبات جاهزة للبيك أب حالياً"
                      : "No orders ready for pickup at the moment"}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentRequestsTableRow;
